import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Close, Add, Delete } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import invoiceService from "../../services/invoiceService";

const METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "EFT" },
  { value: "card", label: "Card" },
];

const blankLine = () => ({
  amount: "",
  method: "bank_transfer",
  transactionId: "",
  notes: "",
});

// Admin-facing "Mark Invoice as Paid" flow — one invoice at a time. Two
// modes: "Paid in Full" records a single payment for the whole remaining
// balance; "Multiple Payments" lets the admin add several payment lines
// (e.g. part cash + part bank transfer, or a series of installments),
// each with its own method and transaction ID, and shows a running total
// against the balance due. If the total received covers the balance the
// invoice is marked paid; otherwise whatever's left over stays as the new
// balance due — same recordPayment endpoint the customer-facing single
// "Pay" flow already uses, just called once per line here.
const MarkInvoicePaidDialog = ({ open, invoice, onClose, onPaid }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [mode, setMode] = useState("full");
  const [fullMethod, setFullMethod] = useState("bank_transfer");
  const [fullTransactionId, setFullTransactionId] = useState("");
  const [fullNotes, setFullNotes] = useState("");
  const [lines, setLines] = useState([blankLine()]);
  const [submitting, setSubmitting] = useState(false);
  const lineIdRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setMode("full");
    setFullMethod("bank_transfer");
    setFullTransactionId("");
    setFullNotes("");
    setLines([{ ...blankLine(), id: ++lineIdRef.current }]);
  }, [open]);

  const balanceDue = invoice?.balanceDue || 0;

  const addLine = () => setLines((prev) => [...prev, { ...blankLine(), id: ++lineIdRef.current }]);
  const removeLine = (id) => setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.id !== id) : prev));
  const setLineField = (id, field) => (e) => {
    const value = e.target.value;
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };

  const totalReceived =
    mode === "full" ? balanceDue : lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const remaining = Math.round((balanceDue - totalReceived) * 100) / 100;
  const overpaid = remaining < 0;
  const willBePaid = mode === "full" || remaining <= 0;

  const canSubmit =
    !submitting &&
    !overpaid &&
    (mode === "full" ? balanceDue > 0 : lines.some((l) => parseFloat(l.amount) > 0));

  const handleSubmit = async () => {
    if (!invoice) return;
    setSubmitting(true);
    try {
      if (mode === "full") {
        await invoiceService.pay(invoice._id, {
          amount: balanceDue,
          method: fullMethod,
          transactionId: fullTransactionId || undefined,
          notes: fullNotes || undefined,
        });
      } else {
        // Sequential, not Promise.all — each call reads-then-writes the
        // same invoice document (amountPaid/balanceDue), so parallel
        // requests would race and clobber each other's update.
        for (const line of lines) {
          const amount = parseFloat(line.amount) || 0;
          if (amount <= 0) continue;
          // eslint-disable-next-line no-await-in-loop
          await invoiceService.pay(invoice._id, {
            amount,
            method: line.method,
            transactionId: line.transactionId || undefined,
            notes: line.notes || undefined,
          });
        }
      }
      enqueueSnackbar(
        willBePaid
          ? "Invoice marked as paid"
          : `Payment recorded — CI$${remaining.toFixed(2)} still due`,
        { variant: "success" },
      );
      onPaid?.();
      onClose();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to record payment", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography fontWeight={700}>Mark Invoice as Paid</Typography>
          <Typography variant="caption" color="text.secondary">
            {invoice?.invoiceNumber} · Balance Due CI${balanceDue.toFixed(2)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={submitting}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel value="full" control={<Radio />} label="Paid in Full" />
          <FormControlLabel value="multiple" control={<Radio />} label="Multiple Payments" />
        </RadioGroup>

        {mode === "full" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Records one payment for the full remaining balance — CI${balanceDue.toFixed(2)}.
            </Alert>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField select label="Payment Method" value={fullMethod} onChange={(e) => setFullMethod(e.target.value)}>
                {METHODS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Transaction ID (optional)"
                value={fullTransactionId}
                onChange={(e) => setFullTransactionId(e.target.value)}
              />
            </Box>
            <TextField
              label="Notes (optional)"
              value={fullNotes}
              onChange={(e) => setFullNotes(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Add one line per payment received (e.g. part cash, part bank transfer, or installments over time).
            </Alert>
            {lines.map((line) => (
              <Box key={line.id} sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  <TextField
                    size="small"
                    label="Amount"
                    type="number"
                    value={line.amount}
                    onChange={setLineField(line.id, "amount")}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    select
                    label="Method"
                    value={line.method}
                    onChange={setLineField(line.id, "method")}
                    sx={{ flex: 1 }}
                  >
                    {METHODS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeLine(line.id)}
                    disabled={lines.length === 1}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
                <TextField
                  size="small"
                  label="Transaction ID (optional)"
                  value={line.transactionId}
                  onChange={setLineField(line.id, "transactionId")}
                />
              </Box>
            ))}
            <Button size="small" startIcon={<Add />} onClick={addLine} sx={{ alignSelf: "flex-start" }}>
              Add Payment
            </Button>

            <Divider />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Total Received
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  CI${totalReceived.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Balance Due
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  CI${balanceDue.toFixed(2)}
                </Typography>
              </Box>
            </Box>

            {overpaid ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                Total received exceeds the balance due by CI${Math.abs(remaining).toFixed(2)} — reduce a line amount.
              </Alert>
            ) : willBePaid ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Total received covers the balance — this invoice will be marked <strong>Paid</strong>.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                CI${remaining.toFixed(2)} will remain as the new balance due after these payments.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {submitting ? <CircularProgress size={18} color="inherit" /> : "Record Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarkInvoicePaidDialog;
